import { serverSupabaseUser } from "#supabase/server"
import { useServerAccessControl, useAccessControl, PUBLIC_PATHS } from '@/composables/useAccessControl'
import { defineAsyncComponent, markRaw } from 'vue'
import { useUiStore } from '@/stores/uiStore'

interface ConfirmAccess {
    allowed: boolean
}

interface RegistrationSettings {
    registration_enabled: boolean
}

export default defineNuxtRouteMiddleware(async (to) => {
    const user = useSupabaseUser()
    const supabase = useSupabaseClient()
    const { isPublicPath } = useAccessControl()
    const uiStore = useUiStore()
    const { setBottomDrawerConfig } = uiStore
    
    // Special check for pitch page
    if (to.path === '/pitch') {
        const allowedUserIds = [
            '9c1b101e-64c5-4e80-8d55-434973b117ff',
            'f72e6e38-df13-4817-809f-8080285a3a68'
        ]
        if (!user.value || !allowedUserIds.includes(user.value.id)) {
            return navigateTo('/')
        }
        return
    }

    // Allow access to public paths or routes with auth: false
    if (isPublicPath(to.path) || to.meta.auth === false) {
        // console.log('going to', to.path)
        return
    }

    // Allow access to auth routes (login, register, etc.)
    if (to.path && typeof to.path === 'string' && to.path.startsWith('/auth/')) {
        return
    }

    // Redirect to login if no user
    if (!user.value) {
        return navigateTo('/login')
    }

    try {
        // Check if user is allowed access
        const { data: confirmAccess, error: accessError } = await supabase
            .from('confirm-access')
            .select('allowed')
            .eq('user_id', user.value.id)
            .single() as { data: ConfirmAccess | null, error: any }

        // Check registration settings
        const { data: registrationSettings, error: regError } = await supabase
            .from('registration_settings')
            .select('registration_enabled')
            .single() as { data: RegistrationSettings | null, error: any }

        // If user has explicit access, allow them regardless of registration settings
        if (confirmAccess?.allowed) {
            // Only check MFA if not already on confirm page to avoid loops
            if (to.path !== '/confirm') {
                // Check MFA status
                const { data: mfaData } = await supabase.auth.mfa.listFactors()
                const hasMfaEnabled = mfaData?.totp && mfaData.totp.length > 0
                
                if (hasMfaEnabled) {
                    // Get current authentication level
                    const { data: authLevel } = await supabase.auth.mfa.getAuthenticatorAssuranceLevel()
                    
                    // If MFA is enabled but not completed, redirect to confirm page
                    if (authLevel?.currentLevel === 'aal1') {
                        return navigateTo('/confirm')
                    }
                }
            }
            return
        }

        // Check if user is on waitlist before denying access
        if (accessError?.code === 'PGRST116' || !confirmAccess?.allowed) {
            const { data: waitlistUser } = await supabase
                .from('users_waitlist')
                .select('email')
                .eq('email', user.value.email || '')
                .maybeSingle()
            
            if (waitlistUser) {
                // User is on waitlist, redirect to a waitlist page or show appropriate message
                // Only redirect if not already on login page to prevent infinite loop
                if (to.path !== '/login') {
                    return navigateTo('/login?message=waitlist')
                }
                return
            }
        }

        // Otherwise, check if access should be denied
        const accessDenied = accessError?.code === 'PGRST116' || 
            !confirmAccess?.allowed || 
            regError?.code === 'PGRST116' || 
            !registrationSettings?.registration_enabled

        if (accessDenied) {
            // Show the waitlist bottom drawer
            setBottomDrawerConfig({
                title: '',
                description: '',
                component: markRaw(defineAsyncComponent(() => import('@/components/GetNotified.vue'))),
                props: {
                    email: user.value.email || ''
                }
            })
            
            // If not on a public path and not already on login page, redirect to login
            if (!isPublicPath(to.path) && to.path !== '/login') {
                return navigateTo('/login?message=access_denied')
            }
        }
    } catch (error) {
        console.error('Auth middleware error:', error)
        // On error, allow navigation to continue to avoid blocking the app
        return
    }
})
